import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * Tenant users for agent selection (GET /users/dropdown). Each row carries the
 * user's team and campaign so the conversation form can filter the agent list
 * by the selected campaign + team (mirrors the KC2 reference behaviour). The
 * human-facing label is the login id, not the extension.
 */
export interface AgentUserOption {
  id: string
  name: string | null
  loginid: string | null
  teamId: string | null
  teamIds: string[]
  campaignId: string | null
  campaignIds: string[]
  /** The user's corresponding agents.id row, if any. Null means this user
   *  isn't an agent and can't be assigned to a conversation. */
  agentId: string | null
}

export interface UseAgentUsersResult {
  users: AgentUserOption[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useAgentUsers(fetchOnMount = false): UseAgentUsersResult {
  const [users, setUsers] = useState<AgentUserOption[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ users: AgentUserOption[] }>('/users/dropdown')
      setUsers(res.users ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch users')
      setUsers([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (fetchOnMount) void refetch()
  }, [fetchOnMount, refetch])

  return { users, loading, error, refetch }
}
